import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  STATUS_LABEL, GOAL_PERIODS, GOAL_PERIOD_LABEL, DEPT_GOAL_METRICS, goalMetric, formatGoalValue, goalGapText, goalTag, deptColor,
  salesChain, contentPiece, financeQueue, fieldEngine, generalHomes, devDashboard,
  formatDurationShort, localToday,
} from '@battleops/shared';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useMetrics } from '@battleops/client/src/data/useMetrics';
import { useMoveHome } from '@battleops/client/src/data/useDeptGoals';
import { useTaskModal } from '../../hooks';
import CounterTiles from '../CounterTiles';
import { DevHoursCard, DevIssuesCard } from './DevDashboard';
import Icon from '../Icon';
import s from './Dept.module.css';

// The web's department dashboard pieces (docs/plans/scores.md "Dashboards"); the phone draws the same from the same
// shared data (packages/mobile/src/components/desk). Engines come from shared/deptengine.js.

export const TONE = { off: 'var(--c-text-faint)', met: '#3FD69B', ahead: '#3FD69B', under: '#3FD69B', on_pace: '#FFD23F', close: '#FFD23F', behind: '#FF7A70', over: '#FF7A70', none: 'var(--c-text-faint)', unset: 'var(--c-text-tertiary)', feed: 'var(--c-text-tertiary)' };
const H = 3600000;

export function Sec({ title, right, children }) {
  return (
    <>
      <div className={s.sec}><h2>{title}</h2>{right ? <span className={s.right}>{right}</span> : null}</div>
      {children}
    </>
  );
}

/** A goal: the number vs its target, the status word, the bar (white line = where you should be by now / the target). */
export function GoalTile({ goal: g, onOpen, onPlus }) {
  const max = g.target ? Math.max(g.target, g.value || 0) * (g.direction === 'down' ? 1.2 : 1) : 1;
  const fill = g.value != null && g.target != null ? Math.max(0, Math.min(100, (g.value / max) * 100)) : 0;
  const markAt = g.target == null ? null : g.kind === 'count' && g.direction === 'up' && g.expected != null ? (g.expected / max) * 100 : (g.target / max) * 100;
  return (
    <div className={`${s.cut} ${s.tile}`} role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      aria-label={`${g.label}: ${formatGoalValue(g.value, g.unit)} of ${formatGoalValue(g.target, g.unit)}, ${STATUS_LABEL[g.status]}`}>
      <div className={s.tileTop}><span className={s.tileName}>{g.label}</span><span className={s.tag}>{goalTag(g)}</span></div>
      <div className={s.nums}>
        <span className={s.big}>{g.status === 'feed' ? '—' : formatGoalValue(g.value, g.unit)}</span>
        {g.target != null ? <span className={s.of}>{g.direction === 'down' ? 'limit ' : '/ '}{formatGoalValue(g.target, g.unit)}</span> : null}
        <span className={s.word} style={{ color: TONE[g.status] }}>{STATUS_LABEL[g.status]}</span>
      </div>
      <div className={s.track}>
        <div className={s.fill} style={{ width: `${fill}%`, background: g.status === 'over' ? TONE.over : undefined }} />
        {markAt != null ? <div className={s.mark} style={{ left: `${Math.min(99.4, markAt)}%` }} /> : null}
      </div>
      <div className={s.foot}>
        <span className={s.gap}>{goalGapText(g)}</span>
        {onPlus ? <button type="button" className={s.plus} onClick={(e) => { e.stopPropagation(); onPlus(); }} aria-label={`Add one to ${g.label}`}><Icon name="plus" size={14} />1</button> : null}
      </div>
    </div>
  );
}

export function MoveRow({ title, why, whyTone, label = 'START', primary, onGo, onOpen, icon = 'play' }) {
  return (
    <div className={`${s.cut} ${s.move}`}>
      <Icon name={icon} size={17} />
      <button type="button" className={s.moveMain} onClick={onOpen}>
        <div className={s.moveTitle}>{title}</div>
        {why ? <div className={s.moveWhy} style={whyTone ? { color: whyTone } : null}>{why}</div> : null}
      </button>
      {onGo ? <button type="button" className={`${s.btn} ${s.cutS} ${primary ? s.primary : ''}`} onClick={onGo}>{label}</button> : null}
    </div>
  );
}

function Bar({ pct, height = 8, tone }) {
  return <div className={s.track} style={{ height }}><div className={s.fill} style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: tone }} /></div>;
}

// ── the goal editor ───────────────────────────────────────────────────────────────────────────────────────────

const TAGS = ['assessment', 'content', 'bug', 'shipped', 'ad', 'flier', 'campaign', 'callback'];

/** What a goal counted and left out, and the rows behind it (the phone's breakdown has the same). */
export function Counted({ goal: g }) {
  if (!g || (!g.basis && !g.notes && !g.rows?.length)) return null;
  return (
    <>
      <span className={s.label}>WHAT IT COUNTED</span>
      {g.basis ? <div className={s.countRow}><Icon name="check-circle" size={14} style={{ color: TONE.met }} /><span>{g.basis}</span></div> : null}
      {(g.notes || []).map((n) => <div key={n} className={s.countRow} style={{ color: 'var(--c-text-tertiary)' }}><Icon name="minus" size={14} /><span>{n}</span></div>)}
      {g.rows?.length ? (
        <div style={{ marginTop: 6 }}>
          {g.rows.slice(0, 6).map((r, i) => <div key={i} className={s.hist}><span className={s.faint} style={{ gridColumn: 'span 2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || r.label || r.note || (r.value != null ? `+${r.value}` : 'entry')}</span><span className={`${s.faint} ${s.num}`} style={{ textAlign: 'right' }}>{r.date || ''}</span></div>)}
          {g.rows.length > 6 ? <div className={s.faint} style={{ fontSize: 12 }}>+{g.rows.length - 6} more</div> : null}
        </div>
      ) : null}
    </>
  );
}

export function GoalEditor({ goal, tasks = [], history = [], presets = null, onSave, onRemove, onClose }) {
  const { business, save: saveMetric } = useMetrics();
  const editing = !!goal?.id && !goal?.starter;
  const [key, setKey] = useState(goal?.key || '');
  const [target, setTarget] = useState(goal?.target != null ? String(goal.target) : '');
  const [period, setPeriod] = useState(goal?.period || 'week');
  const [direction, setDirection] = useState(goal?.direction || 'up');
  const [taskId, setTaskId] = useState(goal?.taskId || '');
  const [counter, setCounter] = useState('');
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  // Field builds from its fixed library only (Lj, Oct 11) — each preset with its period, start target and direction
  const choices = useMemo(() => (presets ? presets.map((p) => ({ key: p.key, label: goalMetric(p.key).label, preset: p })) : [
    ...Object.entries(DEPT_GOAL_METRICS).map(([k, m]) => ({ key: k, label: m.label })),
    ...TAGS.map((t) => ({ key: `tag:${t}`, label: goalMetric(`tag:${t}`).label })),
    ...business.filter((m) => !m.derived).map((m) => ({ key: `metric:${m.id}`, label: m.name })),
  ]), [business, presets]);
  const metric = key ? goalMetric(key, { metrics: business }) : null;
  const n = Number(String(target).replace(',', '.'));
  const ok = !!key && (target === '' || Number.isFinite(n));
  const pick = (k) => {
    setKey(k);
    const m = goalMetric(k, { metrics: business });
    const p = presets?.find((x) => x.key === k);
    setDirection(p?.direction || m?.direction || 'up');
    if (p) { setPeriod(p.period); setTarget(p.target != null ? String(p.target) : ''); }
  };
  const addCounter = () => {
    const name = counter.trim(); if (!name) return;
    const m = saveMetric({ name, unit: '', kind: 'count', agg: 'sum', source: 'manual', attachTo: ['person', 'org'] });
    if (m?.id) pick(`metric:${m.id}`);
    setCounter('');
  };
  return (
    <div className={s.scrim} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={s.sheet} role="dialog" aria-modal="true" aria-label={editing ? 'Edit goal' : 'New goal'}>
        <div className={s.eyebrow}>{editing ? 'GOAL' : 'NEW GOAL'}</div>
        <div className={s.title}>{metric?.label || 'What should it count?'}</div>
        {metric?.hint ? <div className={s.faint}>{metric.hint}</div> : null}
        {!editing && !goal?.key ? (
          <>
            <label className={s.label} htmlFor="goal-metric">COUNT</label>
            <select id="goal-metric" className={s.select} value={key} onChange={(e) => pick(e.target.value)}>
              <option value="">{presets ? 'Pick from the set…' : 'Pick a metric…'}</option>
              {choices.map((c) => <option key={c.key} value={c.key}>{c.label}{c.preset ? ` · ${GOAL_PERIOD_LABEL[c.preset.period].toLowerCase()} · ${c.preset.direction === 'down' ? 'limit ' : ''}${formatGoalValue(c.preset.target, goalMetric(c.key).unit)}` : ''}</option>)}
            </select>
            {presets ? <div className={s.faint} style={{ marginTop: 6 }}>Each one is counted from {presets.some((p) => p.key === 'tag:shipped') ? 'your tasks’ tags (#shipped, #bug, #improvement, #deploy) and your clock' : 'your clock, tasks, photos and job costs'} — the goal shows what it counted and what it left out.</div> : (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input className={s.select} value={counter} onChange={(e) => setCounter(e.target.value)} placeholder="Or a new counter (e.g. Doors knocked)" onKeyDown={(e) => { if (e.key === 'Enter') addCounter(); }} />
                <button type="button" className={s.btn} onClick={addCounter}>ADD</button>
              </div>
            )}
          </>
        ) : null}
        {key ? (
          <>
            <label className={s.label} htmlFor="goal-target">{direction === 'down' ? 'LIMIT' : 'TARGET'}{metric?.unit ? ` (${metric.unit})` : ''}</label>
            <input id="goal-target" className={s.input} inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="No target yet" autoFocus />
            <span className={s.label}>PERIOD</span>
            <div className={s.chips}>{GOAL_PERIODS.map((p) => <button key={p} type="button" className={`${s.chip} ${period === p ? s.chipOn : ''}`} aria-pressed={period === p} onClick={() => setPeriod(p)}>{GOAL_PERIOD_LABEL[p]}</button>)}</div>
            {presets ? null : <div className={s.chips} style={{ marginTop: 8 }}>{[['up', 'More is better'], ['down', 'A limit']].map(([d, l]) => <button key={d} type="button" className={`${s.chip} ${direction === d ? s.chipOn : ''}`} aria-pressed={direction === d} onClick={() => setDirection(d)}>{l}</button>)}</div>}
            <label className={s.label} htmlFor="goal-task">THE TASK THAT DOES THE WORK</label>
            <select id="goal-task" className={s.select} value={taskId} onChange={(e) => setTaskId(e.target.value)}>
              <option value="">None</option>
              {tasks.slice(0, 200).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            {editing ? <Counted goal={goal} /> : null}
            {history.length ? (
              <>
                <span className={s.label}>EARLIER</span>
                {history.map((h) => (
                  <div key={h.from} className={s.hist}>
                    <span className={`${s.faint} ${s.num}`}>{h.from === h.to ? h.from : `${h.from} → ${h.to}`}</span>
                    <b className={s.num}>{formatGoalValue(h.value, metric?.unit)}</b>
                    <span style={{ color: TONE[h.status], fontSize: 11, fontWeight: 800, textAlign: 'right' }}>{STATUS_LABEL[h.status]}</span>
                  </div>
                ))}
              </>
            ) : null}
          </>
        ) : null}
        <div className={s.actions}>
          {editing ? <button type="button" className={`${s.ghost} ${s.danger}`} onClick={onRemove}>Remove</button> : <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>}
          <button type="button" className={s.primary} disabled={!ok} style={!ok ? { opacity: 0.45 } : null}
            onClick={() => onSave({ ...(editing ? { id: goal.id } : {}), key, target: target === '' ? null : n, period, direction, taskId: taskId || null })}>Save goal</button>
        </div>
      </div>
    </div>
  );
}

// ── engines ───────────────────────────────────────────────────────────────────────────────────────────────────

export function Engine({ dept, view, data }) {
  const role = view?.role;
  if (role === 'sales') return <Sales dept={dept} data={data} />;
  if (role === 'marketing') return <Marketing data={data} />;
  if (role === 'finance') return <Finance data={data} />;
  if (role === 'dev') return <Dev dept={dept} data={data} />;
  if (role === 'field') return <Field dept={dept} data={data} />;
  if (role === 'personal') return <Personal />;
  return <General dept={dept} data={data} />;
}

function Sales({ dept, data }) {
  const { log } = useMetrics();
  const c = useMemo(() => salesChain(dept, data), [dept, data]);
  const max = Math.max(1, ...c.rows.map((r) => Number(r.value) || 0));
  return (
    <Sec title="The chain" right={c.source === 'books' ? 'leads → won: books, last 90 days' : 'this month'}>
      <div className={s.cut} style={{ padding: '6px 14px' }}>
        {c.rows.map((r, i) => (
          <div key={r.key}>
            <div className={s.chainRow}>
              <span className={s.chainLabel} style={r.strong ? { color: 'var(--c-text)', fontWeight: 700 } : null}>{r.label}</span>
              <Bar pct={((Number(r.value) || 0) / max) * 100} />
              <span className={s.chainVal}>{r.value == null ? (r.note || '—') : formatGoalValue(r.value)}{r.src === '90d' && r.value != null ? <span className={s.faint} style={{ fontSize: 10.5, fontWeight: 400 }}> 90d</span> : null}</span>
            </div>
            {i < c.rows.length - 1 && c.drops[i] != null ? <div className={s.drop}>↓ {c.drops[i]}%{i === c.worst ? <b style={{ color: TONE.behind }}>BIGGEST DROP</b> : null}</div> : null}
          </div>
        ))}
      </div>
      {c.hasOutreach ? <button type="button" className={`${s.bigBtn} ${s.cutS}`} onClick={() => log({ metricId: 'outreach', value: 1 })}><Icon name="plus" size={16} />OUTREACH TOUCH</button> : null}
    </Sec>
  );
}

function Marketing({ data }) {
  const details = useTaskModal();
  const { piece, steps, nextStep, days, hit, channels } = useMemo(() => contentPiece(data), [data]);
  return (
    <>
      <Sec title="The piece in motion">
        <div className={s.cut} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {piece ? (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div className={s.pieceTitle} style={{ flex: 1 }}>{piece.title}</div>
                {nextStep ? <span className={s.pill}>TODAY: {String(nextStep.title).toUpperCase().slice(0, 16)}</span> : null}
              </div>
              {steps.length ? (
                <div className={s.steps}>
                  {steps.slice(0, 6).map((st) => (
                    <div key={st.id} className={s.step} style={st.id === nextStep?.id ? { color: 'var(--c-text)' } : null}>
                      <div className={s.stepBar} style={{ background: st.status === 'done' ? 'var(--a)' : st.id === nextStep?.id ? 'color-mix(in srgb, var(--a) 45%, transparent)' : undefined }} />
                      <span>{st.title}</span>
                    </div>
                  ))}
                </div>
              ) : <div className={s.faint}>Add steps under it (Idea, Script, Record, Edit, Post) to track the one-a-day rhythm.</div>}
              <button type="button" className={`${s.bigBtn} ${s.cutS}`} style={{ marginTop: 0 }} onClick={() => details.open((nextStep || piece).id)}>{nextStep ? 'START TODAY’S STEP' : 'OPEN THE PIECE'}</button>
            </>
          ) : <div className={s.faint}>Tag a task #content to make it the piece in motion.</div>}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span className={s.faint}>One step a day · last 14 days</span><b className={s.num} style={{ color: hit >= 10 ? TONE.met : undefined }}>{hit} / 14</b></div>
            <div className={s.strip}>{days.map((x) => <div key={x.date} title={x.date} className={`${s.day} ${x.active ? s.dayOn : ''}`} />)}</div>
          </div>
        </div>
      </Sec>
      {channels.length ? (
        <Sec title="Channels" right="books · ranked by profit">
          <div className={s.cut} style={{ padding: '0 14px' }}>
            <table className={s.table}>
              <thead><tr><th>Channel</th><th className={s.r}>Leads</th><th className={s.r}>$/lead</th><th className={s.r}>Profit</th></tr></thead>
              <tbody>{channels.map((r) => (
                <tr key={r.source}><td style={{ fontWeight: 600 }}>{r.name}</td><td className={s.r}>{r.leads}</td><td className={s.r}>{r.costPerLead == null ? '—' : formatGoalValue(r.costPerLead, '$')}</td><td className={s.r} style={{ color: r.profit < 0 ? TONE.over : TONE.met }}>{formatGoalValue(r.profit, '$')}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </Sec>
      ) : null}
    </>
  );
}

function Finance({ data }) {
  const navigate = useNavigate();
  const q = useMemo(() => financeQueue(data), [data]);
  const maxBal = Math.max(1, ...q.weeks.map((w) => Math.abs(w.balance)));
  return (
    <>
      <Sec title="Queue to zero" right={`${q.left} left`}>
        <div className={s.cut} style={{ padding: '2px 14px' }}>
          {q.rows.map((r, i) => (
            <div key={r.key} className={s.qRow}>
              <span className={s.qN}>{String(i + 1).padStart(2, '0')}</span>
              <div style={{ minWidth: 0 }}><div className={s.qTitle}>{r.title}</div><div className={s.qSub} style={r.late ? { color: TONE.over, fontWeight: 600 } : null}>{r.sub}</div></div>
              <span className={s.qCount}>{r.count}</span>
              {r.count && r.link ? <button type="button" className={`${s.btn} ${s.cutS}`} onClick={() => navigate('/cfo')}>OPEN</button>
                : <span className={s.qDone} style={{ color: r.count ? TONE.close : TONE.met }}>{r.count ? 'TO DO' : 'DONE'}</span>}
            </div>
          ))}
        </div>
      </Sec>
      <Sec title="Cash · next 12 weeks" right={<a className={s.link} href="/cfo" onClick={(e) => { e.preventDefault(); navigate('/cfo'); }}>CFO view</a>}>
        <div className={s.cut} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {q.cash ? (
            <>
              <div className={s.stats}>
                <div className={s.stat}><span className={s.kick}>Bank</span><span className={s.statV}>{formatGoalValue(q.cash.bank, '$')}</span></div>
                <div className={s.stat}><span className={s.kick}>Runway</span><span className={s.statV} style={q.cash.runwayWeeks != null && q.cash.runwayWeeks < 13 ? { color: TONE.close } : null}>{q.cash.runwayWeeks == null ? '—' : `${q.cash.runwayWeeks} wks`}</span></div>
                <div className={s.stat}><span className={s.kick}>Low week</span><span className={s.statV} style={q.cash.low?.balance < 0 ? { color: TONE.over } : null}>{q.cash.low ? formatGoalValue(q.cash.low.balance, '$') : '—'}</span></div>
              </div>
              {q.weeks.length ? (
                <div className={s.bars} role="img" aria-label={`Cash by week for ${q.weeks.length} weeks${q.cash.low ? `, lowest ${formatGoalValue(q.cash.low.balance, '$')} in week ${q.cash.low.week}` : ''}`}>
                  {q.weeks.map((w) => <div key={w.week} title={`Week ${w.week}${w.from ? ` (${w.from})` : ''}: ${formatGoalValue(w.balance, '$')}`} style={{ height: `${Math.max(4, (Math.abs(w.balance) / maxBal) * 100)}%`, background: w.balance < 0 ? TONE.over : w.low ? TONE.close : undefined }} />)}
                </div>
              ) : null}
            </>
          ) : <div className={s.faint}>The books haven’t published cash yet.</div>}
        </div>
      </Sec>
    </>
  );
}

/** Dev's engine on the department page: Home's 14-day hours by app and the issues card (DevDashboard.jsx). */
function Dev({ dept, data }) {
  const details = useTaskModal();
  const x = useMemo(() => devDashboard(dept, data), [dept, data]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 20 }}>
      <DevHoursCard x={x} />
      <DevIssuesCard x={x} onOpen={(id) => details.open(id)} />
    </div>
  );
}

function Field({ dept, data }) {
  const details = useTaskModal();
  const navigate = useNavigate();
  const { clockedIn, activeTaskId, startQuickTask, clockIn } = useTasks();
  const [err, setErr] = useState(null);
  // one shape for the phone and the web (shared fieldEngine): on site = the job's live card; not on site = today's jobs
  const f = useMemo(() => fieldEngine(dept, data, { clock: clockedIn, activeTaskId }), [dept, data, clockedIn, activeTaskId]);
  const site = f.site;
  const burn = site?.burn;
  const pace = burn?.pace;
  const over = burn?.usedShare != null && burn.usedShare > burn.doneShare * 1.05 && burn.usedShare > 0;
  const today = localToday();
  return (
    <>
      <Sec title={f.onSite ? 'On site now' : 'Today’s jobs'}>
        <div className={s.cut} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14, background: f.onSite ? 'var(--c-surface-alt)' : undefined, borderColor: f.onSite ? 'var(--a)' : undefined }}>
          {f.onSite ? (
            <>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" style={{ flex: 1, minWidth: 0, textAlign: 'left' }} onClick={() => navigate(`/projects/${site.project.id}`)}>
                  <div className={s.pieceTitle}>{site.project.name}</div>
                  <div className={s.faint} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: site.running ? 'var(--a)' : 'var(--c-text-faint)' }} />{site.running ? site.running.title : 'No task running'}</div>
                </button>
                {clockedIn?.startedAt ? <div style={{ textAlign: 'right' }}><div className={s.num} style={{ fontSize: 20, fontWeight: 700, color: 'var(--a)' }}>{formatDurationShort(Date.now() - clockedIn.startedAt)}</div><div className={s.kick} style={{ fontSize: 10 }}>On the clock</div></div> : null}
              </div>
              {burn && burn.estMin ? (
                <div role="img" aria-label={`Work done ${Math.round(burn.doneShare * 100)}%, hours used ${Math.round((burn.usedShare || 0) * 100)}% of the estimate`} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className={s.paceHead}><span>Work done</span><b className={s.num}>{Math.round(burn.doneShare * 100)}%</b></div>
                  <Bar pct={burn.doneShare * 100} height={10} />
                  <div className={s.paceHead}><span>Hours used</span><b className={s.num} style={over ? { color: TONE.close } : null}>{Math.round(burn.usedMs / H)} of {Math.round(burn.estMin / 60)} h</b></div>
                  <Bar pct={(burn.usedShare || 0) * 100} height={10} tone={over ? TONE.close : '#9FB6CC'} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: pace == null ? 'var(--c-text-tertiary)' : pace > 1.05 ? TONE.close : TONE.met }}>{pace == null ? 'Nothing finished yet — the pace shows once a task with an estimate is done' : pace > 1.05 ? `${Math.round((pace - 1) * 100)}% over the estimate’s pace` : 'On the estimate’s pace'}</span>
                </div>
              ) : <div className={s.faint}>No estimates on this job’s tasks yet.</div>}
              {f.quick.length ? <div className={s.quick}>{f.quick.slice(0, 6).map((q) => <button key={q.id} type="button" className={`${s.qt} ${s.cutS}`} onClick={() => { setErr(null); try { startQuickTask(dept.id, q.id); } catch (e) { setErr(String(e?.message || e)); } }}><Icon name="play" size={13} />{q.title}</button>)}</div> : null}
              {err ? <div style={{ color: 'var(--c-danger)', fontSize: 13 }}>{err}</div> : null}
            </>
          ) : f.jobs.length ? (
            f.jobs.slice(0, 5).map(({ project: p, n, overdue, next }, i) => (
              <div key={p.id} className={s.ver} style={{ minHeight: 56 }}>
                <button type="button" style={{ flex: 1, minWidth: 0, textAlign: 'left' }} onClick={() => navigate(`/projects/${p.id}`)}>
                  <div className={s.qTitle}>{p.name}</div>
                  <div className={s.qSub} style={overdue ? { color: TONE.close } : null}>{n} task{n === 1 ? '' : 's'} today{overdue ? ` · ${overdue} overdue` : ''}{next ? ` · first: ${next.title}` : ''}</div>
                </button>
                <button type="button" className={`${s.btn} ${s.cutS} ${i === 0 ? s.primary : ''}`} onClick={() => clockIn(p.id)}>START</button>
              </div>
            ))
          ) : <div className={s.faint}>No Field work planned today. Clock into a job from its page, or plan the day.</div>}
        </div>
      </Sec>
      {site?.list?.length ? (
        <Sec title="Today on this job" right={`${site.list.filter((t) => t.status === 'done').length} of ${site.list.length}`}>
          {site.list.map((t) => {
            const late = t.status !== 'done' && t.scheduledDate && t.scheduledDate < today;
            return (
              <button key={t.id} type="button" className={`${s.row} ${s.rowBtn}`} onClick={() => details.open(t.id)}>
                <span className={s.box} style={t.status === 'done' ? { background: 'var(--a)', borderColor: 'var(--a)' } : t.id === activeTaskId ? { borderColor: 'var(--a)' } : null} />
                <span className={s.rowText} style={t.status === 'done' ? { color: 'var(--c-text-faint)', textDecoration: 'line-through' } : null}>{t.title}</span>
                <span className={`${s.faint} ${s.num}`} style={t.id === activeTaskId ? { color: 'var(--a)' } : late ? { color: TONE.close } : null}>{t.id === activeTaskId ? 'RUNNING' : late ? `overdue · ${t.scheduledDate}` : t.estimatedMinutes ? `est ${formatDurationShort(t.estimatedMinutes * 60000)}` : ''}</span>
              </button>
            );
          })}
        </Sec>
      ) : null}
    </>
  );
}

function General({ dept, data }) {
  const details = useTaskModal();
  const move = useMoveHome();
  const [moved, setMoved] = useState(null);
  const { homes, oldest } = useMemo(() => generalHomes(dept, data), [dept, data]);
  return (
    <>
      <p className={s.faint} style={{ fontSize: 14, lineHeight: 1.5, marginTop: 20 }}>General is the catch-all. A good week here is a smaller General: work moves to the department it belongs to.</p>
      <Sec title="Find it a home">
        {moved ? <div style={{ color: TONE.met, fontSize: 13, marginBottom: 8 }}>Moved: {moved}</div> : null}
        {homes.length ? (
          <div className={s.cut} style={{ padding: '0 14px' }}>
            {homes.slice(0, 8).map((h) => (
              <div key={h.project.id} className={s.ver} style={{ minHeight: 60 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={s.qTitle}>{h.project.name} · {h.open}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}><span className={s.faint}>belongs in</span><span className={s.to} style={{ background: deptColor(h.to) }}>{String(h.to.name).toUpperCase()}</span></div>
                </div>
                <button type="button" className={`${s.btn} ${s.cutS}`} onClick={async () => { const k = await move(h.project, h.to.id, dept.id); setMoved(`${h.project.name} → ${h.to.name} · ${k} task${k === 1 ? '' : 's'}`); }}>MOVE</button>
              </div>
            ))}
          </div>
        ) : <div className={s.faint}>Nothing here looks like it belongs elsewhere.</div>}
      </Sec>
      {oldest.length ? (
        <Sec title="Oldest first · do, move or drop">
          {oldest.map(({ task: t, ageDays }) => (
            <button key={t.id} type="button" className={`${s.row} ${s.rowBtn}`} onClick={() => details.open(t.id)}>
              <span className={s.num} style={{ width: 44, fontSize: 13, fontWeight: 700, color: ageDays > 30 ? TONE.over : ageDays > 14 ? TONE.close : 'var(--c-text-tertiary)' }}>{ageDays} d</span>
              <span className={s.rowText}>{t.title}</span>
            </button>
          ))}
        </Sec>
      ) : null}
    </>
  );
}

function Personal() {
  const navigate = useNavigate();
  // its projects are cards now (PinnedProjects under the engine, v0.1.256) — the plain list is gone
  return (
    <>
      <div className={s.faint} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 20 }}><Icon name="lock" size={13} />Private · only you · never mixed with business numbers</div>
      <Sec title="Counters · click to count, hold for a number" right={<a className={s.link} href="/track" onClick={(e) => { e.preventDefault(); navigate('/track'); }}>Track</a>}>
        <CounterTiles />
      </Sec>
    </>
  );
}
